import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Box,
  Button,
  Snackbar,
  Alert,
  Typography,
  TextField,
  Container,
  Card,
  CardContent,
  Divider,
  Chip,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import autoGradeClient from "../../../../utils/graphql/autoGradeClient";
import { GET_ALL_EXAM_PAPERS } from "../../../../utils/graphql/services/ai-evaluation/queries";
import { useParams, useHistory } from "react-router-dom";
import SectionForm from "./questonsInfo/SectionForm";
import ExamPaperInfo from "./questonsInfo/ExamPaperInfo";
import { CREATE_EXAM_PAPER_QUESTIONS } from "utils/graphql/services/ai-evaluation/mutations";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

function AddExamPaperQuestions() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const { data, loading, error } = useQuery(GET_ALL_EXAM_PAPERS, {
    client: autoGradeClient,
    variables: { input: { id: parseInt(id) } },
  });

  const [createQuestions] = useMutation(CREATE_EXAM_PAPER_QUESTIONS, {
    client: autoGradeClient,
  });

  const [examPaper, setExamPaper] = useState<any>(null);
  const [sections, setSections] = useState<any[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning"
  >("warning");

  const [paperChoiceCount, setPaperChoiceCount] = useState<number>(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data?.getExamPapers?.examPapers) {
      const paper =
        data.getExamPapers.examPapers.find((p: any) => p.id === id) ||
        data.getExamPapers.examPapers[0];
      setExamPaper(paper);
    }
  }, [data, id]);

  const handleAddSection = () => {
    if (!examPaper) {
      setErrorMsg("Exam paper not loaded yet");
      setSnackbarSeverity("warning");
      return;
    }
    if (sections.length >= examPaper.totalSections) {
      setErrorMsg(`You can only add ${examPaper.totalSections} sections`);
      setSnackbarSeverity("warning");
      return;
    }
    setSections((prev) => [
      ...prev,
      {
        title: "",
        order: prev.length + 1,
        marks: 1,
        totalQuestions: 0,
        choiceCount: 1,
        subject: "",
        subjectName: "",
        questions: [],
      },
    ]);
  };

  const updateSection = (index: number, newData: any) => {
    setSections((prev) => {
      const copy = [...prev];
      copy[index] = newData;
      return copy;
    });
  };

  const removeSection = (index: number) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const generateFlattenedQuestions = () => {
    if (!examPaper) return [];

    const result: any[] = [];

    result.push({
      examPaperId: Number(examPaper.id),
      sectionTitle: "",
      sectionOrder: null,
      questionNumber: "",
      questionType: "",
      marks: examPaper.totalMarks || 0,
      questionSortOrder: null,
      subjectId: "",
      subjectName: "",
      chapterId: "",
      chapterName: "",
      topicId: "",
      topicName: "",
      nodeType: "Paper",
      choiceCount: paperChoiceCount,
    });

    // 2. Then sections and their questions
    sections.forEach((sec, secIdx) => {
      // Add Section object
      result.push({
        examPaperId: Number(examPaper.id),
        sectionTitle: sec.title || "",
        sectionOrder: sec.order || secIdx + 1,
        questionNumber: "",
        questionType: "",
        marks: sec.marks,
        questionSortOrder: null,
        subjectId: "",
        subjectName: "",
        chapterId: "",
        chapterName: "",
        topicId: "",
        topicName: "",
        nodeType: "Section",
        choiceCount: sec.choiceCount || 1,
      });

      sec.questions.forEach((q: any, qIdx: number) => {
        result.push({
          examPaperId: Number(examPaper.id),
          sectionTitle: sec.title || "",
          sectionOrder: sec.order || secIdx + 1,
          questionNumber: (q.qNo || qIdx + 1).toString(),
          questionType: q.type || "",
          marks: q.marks ? Number(q.marks) : null,
          questionSortOrder: q.sortOrder || qIdx + 1,
          nodeType: q.nodeType || "MainQuestion",
          choiceCount: q.choiceCount || 1,
          subjectId: q.subject || "",
          subjectName: q.subjectName || "",
          chapterId: q.chapter || "",
          chapterName: q.chapterName || "",
          topicId: q.topic || "",
          topicName: q.topicName || "",
        });
      });
    });

    return result;
  };

  const handleSave = async () => {
    const flattened = generateFlattenedQuestions();
    if (flattened.length <= 1) {
      setErrorMsg("Please add sections and questions before saving");
      setSnackbarSeverity("warning");
      return;
    }

    setSaving(true);
    try {
      await createQuestions({ variables: { input: flattened } });
      setSuccessMsg("Exam paper questions created successfully!");
      setSnackbarSeverity("success");
      setTimeout(() => history.push("/ai-evaluation/exam-paper"), 1500);
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err.graphQLErrors?.[0]?.message ||
        err.networkError?.message ||
        err.message ||
        "Failed to create exam paper questions. Please try again.";
      setErrorMsg(errorMessage);
      setSnackbarSeverity("error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>Error loading exam paper</Typography>;
  if (!examPaper) return <Typography>No exam paper found</Typography>;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: "primary.main",
            mb: 1,
          }}
        >
          Create Questions
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Exam Paper:
          <Chip
            label={examPaper.title}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card
            elevation={2}
            sx={{
              mb: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Exam Paper Details
              </Typography>
              <ExamPaperInfo
                examPapers={[examPaper]}
                loading={loading}
                selectedId={examPaper.id}
                setSelectedId={() => { }}
              />
            </CardContent>
          </Card>

          <Card
            elevation={2}
            sx={{
              mb: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Paper Level Configuration
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Total Marks"
                    type="number"
                    size="small"
                    fullWidth
                    value={examPaper.totalMarks || 0}
                    disabled
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Choice Count"
                    type="number"
                    size="small"
                    fullWidth
                    value={paperChoiceCount}
                    onChange={(e) =>
                      setPaperChoiceCount(Number(e.target.value))
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              Sections ({sections.length}/{examPaper.totalSections})
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAddSection}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              Add Section
            </Button>
          </Box>
          {sections.length === 0 ? (
            <Card
              elevation={1}
              sx={{
                borderRadius: 2,
                border: "2px dashed",
                borderColor: "divider",
                backgroundColor: "grey.50",
              }}
            >
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    mb: 2,
                  }}
                >
                  No sections added yet
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Click "Add Section" to get started
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ space: 2 }}>
              {sections.map((sec, idx) => (
                <Box key={idx} sx={{ mb: 3 }}>
                  <SectionForm
                    index={idx}
                    data={sec}
                    onChange={(newData) => updateSection(idx, newData)}
                    onRemove={() => removeSection(idx)}
                    examPaper={examPaper}
                  />
                </Box>
              ))}
            </Box>
          )}

          {sections.length > 0 && (
            <Card
              elevation={2}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                mt: 4,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={() => history.goBack()}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={
                      saving ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 4,
                    }}
                  >
                    {saving ? "Creating..." : "Create Questions"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={snackbarSeverity === "error" ? 8000 : 6000}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMsg("")}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={6000}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMsg("")}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AddExamPaperQuestions;
